import { create } from "zustand";

import { dailyChallengesService } from "../services/DailyChallengesService";
import type {
  DailyChallenge,
  DailyChallengeProgress,
} from "../types/dailyChallenges";

interface DailyChallengeState {
  hasAccess: boolean;
  todaysChallenge: DailyChallenge | null;
  hasPlayedToday: boolean;
  remainingFreeGames: number;
  progress: Record<string, DailyChallengeProgress>;

  isLoading: boolean;
  error: string | null;

  fetchDailyChallengeState: () => Promise<void>;
  refreshGameAccessState: () => Promise<{ success: boolean; error?: string }>;
  consumeFreeGame: () => Promise<void>;
  markChallengeCompleted: () => void;
}

export const useDailyChallengeStore = create<DailyChallengeState>((set) => ({
  hasAccess: true,
  todaysChallenge: null,
  hasPlayedToday: false,
  remainingFreeGames: 0,
  progress: {},
  isLoading: true,
  error: null,

  fetchDailyChallengeState: async () => {
    // This will be called on app start
    set({ isLoading: true, error: null });
    try {
      const state = await dailyChallengesService.getDailyChallengeState();
      set({
        hasAccess: state.hasAccess,
        todaysChallenge: state.todaysChallenge,
        hasPlayedToday: state.hasPlayedToday,
        remainingFreeGames: state.remainingFreeGames,
        progress: state.progress,
        isLoading: false,
      });
    } catch (e) {
      console.error("Failed to fetch daily challenge state:", e);
      set({
        isLoading: false,
        error: "Failed to fetch daily challenge state.",
      });
    }
  },

  refreshGameAccessState: async () => {
    console.log("🔄 Refreshing game access state in new store...");
    set({ isLoading: true });
    try {
      const dailyChallengeState =
        await dailyChallengesService.getDailyChallengeState();

      set({
        hasAccess: dailyChallengeState.hasAccess,
        todaysChallenge: dailyChallengeState.todaysChallenge,
        hasPlayedToday: dailyChallengeState.hasPlayedToday,
        remainingFreeGames: dailyChallengeState.remainingFreeGames,
        progress: dailyChallengeState.progress,
        isLoading: false,
      });

      const hasGameAccess =
        dailyChallengeState.hasAccess ||
        dailyChallengeState.remainingFreeGames > 0;
      if (hasGameAccess) {
        console.log("🎮 User has game access");
      }

      console.log("✅ Game access state refreshed successfully");
      return { success: true };
    } catch (error) {
      console.error("❌ Error refreshing game access state:", error);
      set({
        isLoading: false,
        error: (error as Error).message || "An unknown error occurred",
      });
      return {
        success: false,
        error: (error as Error).message || "An unknown error occurred",
      };
    }
  },

  consumeFreeGame: async () => {
    await dailyChallengesService.consumeFreeGame();
    const remainingFreeGames =
      await dailyChallengesService.getRemainingFreeGames();
    set({ remainingFreeGames });
  },

  // This is for client-side optimistic updates. The authoritative "completed" status comes from unifiedDataStore via the service.
  markChallengeCompleted: () => {
    set({ hasPlayedToday: true });
  },
}));
